// frontend/src/admin/api.js
import client from "../api/client";

/**
 * Admin API — thin wrappers around the superadmin-only /api/admin/* endpoints.
 * Reuses the shared axios client (Bearer + CSRF + same-origin) so admin calls
 * carry the operator's JWT exactly like the rest of the app.
 */

/** GET /api/admin/dashboard/stats — platform-wide headline numbers. */
export async function getAdminStats() {
  const { data } = await client.get("/api/admin/dashboard/stats");
  return data;
}

/** GET /api/admin/celery/stats — Celery broker queue depth + live worker count. */
export async function getCeleryStats() {
  const { data } = await client.get("/api/admin/celery/stats");
  return data;
}

// --------------------------------------------------------------------------- //
// Pricing (PRICING-ADMIN-PLAN P2)                                             //
// --------------------------------------------------------------------------- //

/**
 * GET /api/admin/pricing — every plan's current/upcoming/scheduled price for
 * one cadence slice. QRFlow sells in EUR only.
 * @param {"monthly"|"annual"} billingPeriod
 */
export async function listPricing(billingPeriod = "monthly") {
  const { data } = await client.get("/api/admin/pricing", {
    params: { billing_period: billingPeriod },
  });
  return data;
}

/**
 * PUT /api/admin/pricing/{planId} — set a plan's price effective now.
 * @param {string} planId
 * @param {{unit_amount:number, note?:string}} body amount in cents (EUR)
 */
export async function setPlanPrice(planId, body) {
  const { data } = await client.put(`/api/admin/pricing/${planId}`, body);
  return data;
}

/**
 * POST /api/admin/pricing/{planId}/schedule — schedule a future price change.
 * @param {string} planId
 * @param {{unit_amount:number, effective_from:string, note?:string, migrate_existing?:boolean}} body
 */
export async function schedulePlanPrice(planId, body) {
  const { data } = await client.post(
    `/api/admin/pricing/${planId}/schedule`,
    body,
  );
  return data;
}

/** DELETE /api/admin/pricing/schedule/{rowId} — cancel a scheduled change. */
export async function cancelScheduledPrice(rowId) {
  await client.delete(`/api/admin/pricing/schedule/${rowId}`);
}

/**
 * POST /api/admin/pricing/{planId}/promo — create a time-boxed discount.
 * @param {string} planId
 * @param {{discount_type:'percent'|'amount', discount_value:number, effective_from:string, effective_until:string, duration_months?:number, note?:string}} body
 */
export async function createPromo(planId, body) {
  const { data } = await client.post(
    `/api/admin/pricing/${planId}/promo`,
    body,
  );
  return data;
}

/** DELETE /api/admin/pricing/promo/{rowId} — end a promo (cancel/expire). */
export async function endPromo(rowId) {
  await client.delete(`/api/admin/pricing/promo/${rowId}`);
}

// --------------------------------------------------------------------------- //
// Site banners / announcements (PRICING-ADMIN-PLAN P5)                         //
// --------------------------------------------------------------------------- //

/** GET /api/admin/banners — all banners, newest first. */
export async function listBanners() {
  const { data } = await client.get("/api/admin/banners");
  return data;
}

/** POST /api/admin/banners — create a banner. */
export async function createBanner(body) {
  const { data } = await client.post("/api/admin/banners", body);
  return data;
}

/** PUT /api/admin/banners/{id} — update a banner. */
export async function updateBanner(id, body) {
  const { data } = await client.put(`/api/admin/banners/${id}`, body);
  return data;
}

/** DELETE /api/admin/banners/{id} — delete a banner. */
export async function deleteBanner(id) {
  await client.delete(`/api/admin/banners/${id}`);
}

/**
 * GET /api/admin/orgs — paginated, searchable org list.
 * @param {{page?:number, per_page?:number, q?:string, plan?:string, status?:string}} params
 */
export async function listOrgs(params = {}) {
  const { data } = await client.get("/api/admin/orgs", { params });
  return data;
}

/** GET /api/admin/orgs/{id} — full org detail (members, billing, usage). */
export async function getOrg(orgId) {
  const { data } = await client.get(`/api/admin/orgs/${orgId}`);
  return data;
}

/** PATCH /api/admin/orgs/{id}/plan — override the org's plan (no Stripe). */
export async function overrideOrgPlan(orgId, planName) {
  const { data } = await client.patch(`/api/admin/orgs/${orgId}/plan`, {
    plan_name: planName,
  });
  return data;
}

/** PATCH /api/admin/orgs/{id}/suspend */
export async function suspendOrg(orgId) {
  const { data } = await client.patch(`/api/admin/orgs/${orgId}/suspend`);
  return data;
}

/** PATCH /api/admin/orgs/{id}/unsuspend */
export async function unsuspendOrg(orgId) {
  const { data } = await client.patch(`/api/admin/orgs/${orgId}/unsuspend`);
  return data;
}

/**
 * POST /api/admin/orgs/{id}/impersonate — mint a 15-min owner token.
 * @returns {Promise<{access_token:string, expires_in:number, impersonated_email:string, org_id:string}>}
 */
export async function impersonateOrg(orgId, reason) {
  const { data } = await client.post(`/api/admin/orgs/${orgId}/impersonate`, {
    reason,
  });
  return data;
}

/** DELETE /api/admin/orgs/{id} — soft-delete (requires slug confirmation). */
export async function deleteOrg(orgId, confirmation) {
  const { data } = await client.delete(`/api/admin/orgs/${orgId}`, {
    data: { confirmation },
  });
  return data;
}

/**
 * GET /api/admin/vouchers — paginated voucher list.
 * @param {{page?:number, per_page?:number, is_active?:boolean, discount_type?:string}} params
 */
export async function listVouchers(params = {}) {
  const { data } = await client.get("/api/admin/vouchers", { params });
  return data;
}

/** POST /api/admin/vouchers — create a voucher (+ Stripe coupon). */
export async function createVoucher(body) {
  const { data } = await client.post("/api/admin/vouchers", body);
  return data;
}

/** GET /api/admin/vouchers/{id} — voucher detail + redemption history. */
export async function getVoucher(voucherId) {
  const { data } = await client.get(`/api/admin/vouchers/${voucherId}`);
  return data;
}

/** PATCH /api/admin/vouchers/{id}/deactivate */
export async function deactivateVoucher(voucherId) {
  const { data } = await client.patch(
    `/api/admin/vouchers/${voucherId}/deactivate`,
  );
  return data;
}

/** GET /api/admin/reports/financial */
export async function getFinancialReport() {
  const { data } = await client.get("/api/admin/reports/financial");
  return data;
}

/** GET /api/admin/reports/usage */
export async function getUsageReport() {
  const { data } = await client.get("/api/admin/reports/usage");
  return data;
}

/** GET /api/admin/reports/feature-adoption */
export async function getFeatureAdoption() {
  const { data } = await client.get("/api/admin/reports/feature-adoption");
  return data;
}

/** GET /api/admin/reports/system */
export async function getSystemReport() {
  const { data } = await client.get("/api/admin/reports/system");
  return data;
}

/** GET /api/admin/ai/health — per-model status of the LiteLLM gateway. */
export async function getAiHealth() {
  const { data } = await client.get("/api/admin/ai/health");
  return data;
}

/** GET /api/admin/ai/spend — this-month LLM spend, by model. */
export async function getAiSpend() {
  const { data } = await client.get("/api/admin/ai/spend");
  return data;
}

/**
 * GET /api/admin/users — paginated user list (search by email via ?q=).
 * @param {{page?:number, per_page?:number, q?:string}} params
 */
export async function listUsers(params = {}) {
  const { data } = await client.get("/api/admin/users", { params });
  return data;
}

/** GET /api/admin/users/{id} — user detail incl. memberships + recent audit. */
export async function getUserDetail(userId) {
  const { data } = await client.get(`/api/admin/users/${userId}`);
  return data;
}

/** PATCH /api/admin/users/{id}/disable — disable a user (revokes sessions). */
export async function disableUser(userId) {
  const { data } = await client.patch(`/api/admin/users/${userId}/disable`);
  return data;
}

/** PATCH /api/admin/users/{id}/enable — re-enable a disabled user. */
export async function enableUser(userId) {
  const { data } = await client.patch(`/api/admin/users/${userId}/enable`);
  return data;
}

/** POST /api/admin/users/{id}/force-password-reset — email a reset link. */
export async function forcePasswordReset(userId, revokeSessions = true) {
  const { data } = await client.post(
    `/api/admin/users/${userId}/force-password-reset`,
    { revoke_sessions: revokeSessions },
  );
  return data;
}

/** POST /api/admin/broadcast — queue an in-app broadcast. */
export async function sendBroadcast(body) {
  const { data } = await client.post("/api/admin/broadcast", body);
  return data;
}

/** POST /api/admin/email-user — direct email to one user. */
export async function sendEmailToUser(body) {
  const { data } = await client.post("/api/admin/email-user", body);
  return data;
}

// ── Knowledge base (P17-G12) ────────────────────────────────────────────────

/**
 * POST /api/admin/knowledge/ingest — upload a markdown/text file for ingestion.
 * @param {File} file
 * @param {string} docType  one of faq|api_doc|pricing|support_case|other
 * @returns {Promise<{task_id:string, chunks_queued:number, source_doc:string}>}
 */
export async function ingestKnowledgeFile(file, docType) {
  const form = new FormData();
  form.append("file", file);
  form.append("doc_type", docType);
  const { data } = await client.post("/api/admin/knowledge/ingest", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

/** POST /api/admin/knowledge/ingest-url — fetch a URL and ingest it. */
export async function ingestKnowledgeUrl(url, docType) {
  const { data } = await client.post("/api/admin/knowledge/ingest-url", {
    url,
    doc_type: docType,
  });
  return data;
}

/**
 * GET /api/admin/knowledge/chunks — paginated chunk listing.
 * @param {{page?:number, per_page?:number, source_doc?:string, doc_type?:string}} params
 */
export async function listKnowledgeChunks(params = {}) {
  const { data } = await client.get("/api/admin/knowledge/chunks", { params });
  return data;
}

/** DELETE /api/admin/knowledge/chunks/{id} — delete a single chunk. */
export async function deleteKnowledgeChunk(id) {
  await client.delete(`/api/admin/knowledge/chunks/${id}`);
}

/** DELETE /api/admin/knowledge/source/{sourceDoc} — delete all chunks for a doc. */
export async function deleteKnowledgeSource(sourceDoc) {
  const { data } = await client.delete(
    `/api/admin/knowledge/source/${encodeURIComponent(sourceDoc)}`,
  );
  return data;
}

/** POST /api/admin/knowledge/search — test retrieval; returns ranked chunks. */
export async function searchKnowledge(body) {
  const { data } = await client.post("/api/admin/knowledge/search", body);
  return data;
}

// ── Support tickets (M4a-A7) ────────────────────────────────────────────────

/**
 * GET /api/admin/tickets — enriched, paginated ticket list across all orgs.
 * @param {{page?:number, per_page?:number, status?:string, org_id?:string,
 *   priority?:string, category?:string, overdue?:boolean}} params
 */
export async function listAdminTickets(params = {}) {
  const { data } = await client.get("/api/admin/tickets", { params });
  return data;
}

/** GET /api/admin/tickets/{id} — full detail with conversation thread. */
export async function getAdminTicket(ticketId) {
  const { data } = await client.get(`/api/admin/tickets/${ticketId}`);
  return data;
}

/** POST /api/admin/tickets/{id}/messages — operator reply. */
export async function addAdminTicketMessage(ticketId, body) {
  const { data } = await client.post(
    `/api/admin/tickets/${ticketId}/messages`,
    { body },
  );
  return data;
}

/** PATCH /api/admin/tickets/{id} — change status and/or priority. */
export async function updateAdminTicket(ticketId, patch) {
  const { data } = await client.patch(`/api/admin/tickets/${ticketId}`, patch);
  return data;
}

// --- Agent learning: human review queue (P18-G5) --------------------------- //

/** GET /api/admin/ai/review-queue — flagged cases awaiting review. */
export async function listReviewQueue(params = {}) {
  const { data } = await client.get("/api/admin/ai/review-queue", { params });
  return data;
}

/** GET /api/admin/ai/review-queue/count — unreviewed count for the nav badge. */
export async function getReviewQueueCount() {
  const { data } = await client.get("/api/admin/ai/review-queue/count");
  return data.count;
}

/** GET /api/admin/ai/review-queue/{id} — full case with transcript. */
export async function getReviewCase(caseId) {
  const { data } = await client.get(`/api/admin/ai/review-queue/${caseId}`);
  return data;
}

/** POST /api/admin/ai/review-queue/{id}/review — record a verdict, clear flag. */
export async function submitCaseReview(caseId, body) {
  const { data } = await client.post(
    `/api/admin/ai/review-queue/${caseId}/review`,
    body,
  );
  return data;
}

// --- Agent learning: prompt versioning (P18-G7/G8) ------------------------- //

/** GET /api/admin/ai/prompts/{agent} — all versions, newest first. */
export async function listPromptVersions(agent) {
  const { data } = await client.get(`/api/admin/ai/prompts/${agent}`);
  return data;
}

/** GET /api/admin/ai/prompts/{agent}/{version} — one version with few-shots. */
export async function getPromptVersion(agent, version) {
  const { data } = await client.get(`/api/admin/ai/prompts/${agent}/${version}`);
  return data;
}

/** POST /api/admin/ai/prompts/{agent} — create a new (inactive) version. */
export async function createPromptVersion(agent, body) {
  const { data } = await client.post(`/api/admin/ai/prompts/${agent}`, body);
  return data;
}

/** POST /api/admin/ai/prompts/{agent}/deploy — regression-test + maybe activate. */
export async function deployPromptVersion(agent, versionId) {
  const { data } = await client.post(`/api/admin/ai/prompts/${agent}/deploy`, {
    version_id: versionId,
  });
  return data;
}

/** GET /api/admin/ai/prompts/{agent}/deploy/{taskId} — regression task status. */
export async function getDeployStatus(agent, taskId) {
  const { data } = await client.get(
    `/api/admin/ai/prompts/${agent}/deploy/${taskId}`,
  );
  return data;
}

// --- AI Models (provider/credential management + model selection, AM2) ------

/** GET /api/admin/ai/providers — configured providers (API keys masked). */
export async function listAiProviders() {
  const { data } = await client.get("/api/admin/ai/providers");
  return data;
}

/** POST /api/admin/ai/providers — register a provider. */
export async function createAiProvider(body) {
  const { data } = await client.post("/api/admin/ai/providers", body);
  return data;
}

/** PATCH /api/admin/ai/providers/{id} — rotate key / change URL / toggle. */
export async function updateAiProvider(id, body) {
  const { data } = await client.patch(`/api/admin/ai/providers/${id}`, body);
  return data;
}

/** DELETE /api/admin/ai/providers/{id} — remove (409 if a role still binds it). */
export async function deleteAiProvider(id) {
  await client.delete(`/api/admin/ai/providers/${id}`);
}

/** POST /api/admin/ai/providers/{id}/test — live test-connection via the gateway. */
export async function testAiProvider(id, body) {
  const { data } = await client.post(`/api/admin/ai/providers/${id}/test`, body);
  return data;
}

/** GET /api/admin/ai/providers/{id}/models — live model list from the provider. */
export async function listProviderModels(id) {
  const { data } = await client.get(`/api/admin/ai/providers/${id}/models`);
  return data; // { models: string[], source: "live"|"error", error?: string }
}

/** GET /api/admin/ai/bindings — current role → provider/model map. */
export async function listAiBindings() {
  const { data } = await client.get("/api/admin/ai/bindings");
  return data;
}

/** PUT /api/admin/ai/bindings/{role} — set the active model for a role (immediate). */
export async function setAiBinding(role, body) {
  const { data } = await client.put(`/api/admin/ai/bindings/${role}`, body);
  return data;
}

/**
 * GET /api/admin/consent — the consent ledger across users (filterable).
 * @param {{ page?:number, per_page?:number, q?:string, document_type?:string }} params
 */
export async function listConsentLog(params = {}) {
  const { data } = await client.get("/api/admin/consent", { params });
  return data;
}
